/**
 * React Query hooks for dataset operations.
 *
 * Includes dashboard-specific hooks that fetch real DB data with
 * country-aware synthetic fallbacks when no data exists.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDatasets,
  getDataset,
  createDataset,
  updateDataset,
  deleteDataset,
  validateCSV,
  uploadValues,
  getValues,
  getValuesStats,
  deleteValues,
  computeCommunityAnnualLoad,
} from '../api/datasets';
import {
  getSeason,
  getCountryDefaults,
  czechTariffProfile,
  czechEnergyBalance,
  czechLoadProfile,
  czechPVProfile,
  estimateBuildingAnnualKwh,
} from '../data/czechEnergyProfiles';

/** Query key factory for datasets. */
export const datasetKeys = {
  all: (filters) => ['datasets', filters],
  detail: (id) => ['datasets', id],
  values: (id) => ['datasetValues', id],
  stats: (id) => ['datasetStats', id],
};

/**
 * List datasets with optional filters.
 * @param {Object} [filters={}] - { community_id?, building_id?, dataset_type? }
 */
export function useDatasets(filters = {}) {
  return useQuery({
    queryKey: datasetKeys.all(filters),
    queryFn: () => getDatasets(filters),
    enabled: !!filters.community_id, // require at least community_id
  });
}

/**
 * Get a single dataset by ID.
 * @param {string|null} datasetId - UUID
 */
export function useDataset(datasetId) {
  return useQuery({
    queryKey: datasetKeys.detail(datasetId),
    queryFn: () => getDataset(datasetId),
    enabled: !!datasetId,
  });
}

/**
 * Validate a CSV file (no storage).
 */
export function useValidateCSV() {
  return useMutation({
    mutationFn: ({ file, datasetType }) => validateCSV(file, datasetType),
  });
}

/**
 * Create a dataset metadata record.
 */
export function useCreateDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createDataset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
}

/**
 * Update a dataset.
 */
export function useUpdateDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ datasetId, data }) => updateDataset(datasetId, data),
    onSuccess: (_, { datasetId }) => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      queryClient.invalidateQueries({ queryKey: datasetKeys.detail(datasetId) });
    },
  });
}

/**
 * Delete a dataset and its values.
 */
export function useDeleteDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDataset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
}

/**
 * Upload CSV time-series values into a dataset.
 */
export function useUploadValues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ datasetId, file }) => uploadValues(datasetId, file),
    onSuccess: (_, { datasetId }) => {
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      queryClient.invalidateQueries({ queryKey: datasetKeys.values(datasetId) });
      queryClient.invalidateQueries({ queryKey: datasetKeys.stats(datasetId) });
    },
  });
}

/**
 * Retrieve time-series values.
 * @param {string|null} datasetId
 * @param {Object} [params={}]
 */
export function useValues(datasetId, params = {}) {
  return useQuery({
    queryKey: [...datasetKeys.values(datasetId), params],
    queryFn: () => getValues(datasetId, params),
    enabled: !!datasetId,
  });
}

/**
 * Get value statistics.
 * @param {string|null} datasetId
 */
export function useValuesStats(datasetId) {
  return useQuery({
    queryKey: datasetKeys.stats(datasetId),
    queryFn: () => getValuesStats(datasetId),
    enabled: !!datasetId,
  });
}

/**
 * Compute total annual load for a community from its building load datasets.
 * Returns null while loading, or the computed value (or null if no data).
 * @param {string|null} communityId
 */
export function useCommunityAnnualLoad(communityId) {
  return useQuery({
    queryKey: ['communityAnnualLoad', communityId],
    queryFn: () => computeCommunityAnnualLoad(communityId),
    enabled: !!communityId,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });
}

// ---------------------------------------------------------------------------
// Dashboard data hooks — fetch real DB data with synthetic fallbacks
// ---------------------------------------------------------------------------

/** Season month ranges for dataset value filtering (0-indexed months). */
const SEASON_MONTHS = {
  winter: [11, 0, 1],   // Dec, Jan, Feb
  spring: [2, 3, 4],    // Mar, Apr, May
  summer: [5, 6, 7],    // Jun, Jul, Aug
  fall:   [8, 9, 10],   // Sep, Oct, Nov
}

/**
 * Get representative dates from the dataset year (2025) for a given season.
 * Returns start_time and end_time for fetching a 7-day window within the season.
 */
function getSeasonalDateRange(season) {
  // Pick middle of the season in 2025 data
  const midMonths = {
    winter: { month: 0, day: 15 },  // Jan 15
    spring: { month: 3, day: 15 },  // Apr 15
    summer: { month: 6, day: 15 },  // Jul 15
    fall:   { month: 9, day: 15 },  // Oct 15
  }
  const mid = midMonths[season] || midMonths.spring
  const start = new Date(2025, mid.month, mid.day - 3)
  const end = new Date(2025, mid.month, mid.day + 4)
  return {
    start_time: start.toISOString(),
    end_time: end.toISOString(),
  }
}

/**
 * Compute average hourly profile (24 values) from an array of time-series values.
 * @param {Array<{time: string, value: number}>} values - Hourly time-series data
 * @returns {number[]} 24-element array of average values per hour-of-day
 */
function computeHourlyAverage(values) {
  const sums = new Array(24).fill(0)
  const counts = new Array(24).fill(0)
  for (const v of values) {
    const hour = new Date(v.time).getUTCHours()
    if (v.value != null && !isNaN(v.value)) {
      sums[hour] += v.value
      counts[hour] += 1
    }
  }
  return sums.map((s, i) => counts[i] > 0 ? +(s / counts[i]).toFixed(3) : 0)
}

/**
 * Fetch tariff profile from DB datasets, falling back to synthetic generation.
 *
 * Logic:
 * 1. Fetch datasets for the community filtered by kind=tariff
 * 2. If tariff dataset(s) exist, fetch values and compute hourly averages
 * 3. If no tariff data, fall back to czechTariffProfile() synthetic generator
 *
 * @param {string|null} communityId
 * @param {object|null} community - Full community object for synthetic fallback
 */
export function useCommunityTariffProfile(communityId, community) {
  return useQuery({
    queryKey: ['communityTariffProfile', communityId],
    queryFn: async () => {
      if (!communityId) return null

      try {
        const allDatasets = await getDatasets({ community_id: communityId })
        // Only look at historical tariff datasets (not day-ahead)
        const historicalTariffDs = (Array.isArray(allDatasets) ? allDatasets : []).filter(
          d => (d.kind === 'tariff' || (d.dataset_type && d.dataset_type.toLowerCase().includes('tariff')))
            && !d.dataset_type?.startsWith('day_ahead_')
        )

        if (historicalTariffDs.length === 0) {
          // No historical tariff data — fall back to community metadata
          return null  // let TariffProfileChart use communityTariffs prop
        }

        const hours = Array.from({ length: 24 }, (_, i) => i)
        const currency = community?.currency || 'EUR'
        let importTariff = null
        let exportTariff = null

        const importDs = historicalTariffDs.find(d =>
          d.dataset_type === 'energy_tariff_data' ||
          (d.kind === 'tariff' && !d.dataset_type?.includes('feed_in'))
        )
        const exportDs = historicalTariffDs.find(d => d.dataset_type === 'feed_in_tariff_data')

        const season = getSeason()
        const dateRange = getSeasonalDateRange(season)

        if (importDs) {
          const vals = await getValues(importDs.id, { ...dateRange, limit: 168 })
          const valuesArr = vals?.values || vals || []
          if (Array.isArray(valuesArr) && valuesArr.length > 0) {
            importTariff = computeHourlyAverage(valuesArr)
          }
        }

        if (exportDs) {
          const vals = await getValues(exportDs.id, { ...dateRange, limit: 168 })
          const valuesArr = vals?.values || vals || []
          if (Array.isArray(valuesArr) && valuesArr.length > 0) {
            exportTariff = computeHourlyAverage(valuesArr)
          }
        }

        if (importTariff) {
          if (!exportTariff) {
            exportTariff = importTariff.map(v => +(v * 0.4).toFixed(4))
          }
          return { hours, importTariff, exportTariff, currency, source: 'historical' }
        }

        return null  // fall back to communityTariffs prop
      } catch {
        return null
      }
    },
    enabled: !!communityId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Compute energy balance from real load/PV data or fall back to synthetic.
 *
 * Uses battery dispatch logic identical to czechEnergyBalance().
 *
 * @param {string|null} communityId
 * @param {object|null} community - Full community object
 */
export function useCommunityEnergyBalance(communityId, community) {
  return useQuery({
    queryKey: ['communityEnergyBalance', communityId],
    queryFn: async () => {
      if (!communityId || !community) return null

      const season = getSeason()
      const buildings = community.buildings || []
      const assets = community.community_assets || []

      // Battery capacity from community + building assets
      const batteryKwh = assets
        .filter(a => ['community_battery', 'battery'].includes(a.asset_type))
        .reduce((sum, a) => sum + (a.specs?.capacity_kwh || 0), 0)
        + buildings.reduce((sum, b) =>
          sum + (b.building_assets || [])
            .filter(a => a.asset_type === 'battery')
            .reduce((s, a) => s + (a.specs?.capacity_kwh || 0), 0)
        , 0)

      try {
        const datasets = await getDatasets({ community_id: communityId })
        const allDatasets = Array.isArray(datasets) ? datasets : []

        const loadDatasets = allDatasets.filter(
          d => d.kind === 'load' || (d.dataset_type && d.dataset_type.toLowerCase().includes('load'))
        )
        const pvDatasets = allDatasets.filter(
          d => d.kind === 'pv' || (d.dataset_type && d.dataset_type.toLowerCase().includes('pv'))
        )

        // Fetch seasonal window (7 days) for averaging
        const dateRange = getSeasonalDateRange(season)
        const communityLatitude = community?.metadata?.latitude || 47

        // --- LOAD: per-building deduplication + synthetic fallback ---
        // Group load datasets by building_id. Multiple datasets for the same building
        // are averaged (handles duplicates and datasets covering different periods).
        // Buildings WITHOUT datasets get synthetic profiles from estimateBuildingAnnualKwh.
        const loadByBuilding = {}
        for (const ds of loadDatasets) {
          const bId = ds.building_id || '_community'
          if (!loadByBuilding[bId]) loadByBuilding[bId] = []
          loadByBuilding[bId].push(ds)
        }

        let aggregatedLoad = new Array(24).fill(0)
        let hasAnyRealLoad = false
        const buildingsWithLoad = new Set()
        // Per-building profiles for per-building energy balance
        const perBuildingLoad = {}
        const perBuildingPV = {}

        // Process buildings that have datasets
        for (const [bId, dsList] of Object.entries(loadByBuilding)) {
          // Fetch all datasets for this building, compute per-dataset hourly averages
          const profiles = []
          for (const ds of dsList) {
            try {
              const vals = await getValues(ds.id, { ...dateRange, limit: 168 })
              const valuesArr = vals?.values || vals || []
              if (Array.isArray(valuesArr) && valuesArr.length > 0) {
                profiles.push(computeHourlyAverage(valuesArr))
              }
            } catch { /* skip failed datasets */ }
          }
          if (profiles.length > 0) {
            // Average across multiple datasets for same building (deduplication)
            const avgProfile = profiles[0].map((_, i) =>
              +(profiles.reduce((sum, p) => sum + p[i], 0) / profiles.length).toFixed(3)
            )
            aggregatedLoad = aggregatedLoad.map((v, i) => v + avgProfile[i])
            hasAnyRealLoad = true
            buildingsWithLoad.add(bId)
            perBuildingLoad[bId] = avgProfile
          }
        }

        // Add synthetic load for buildings WITHOUT datasets,
        // but only if there are no community-level datasets covering all buildings.
        const hasCommunityLevelLoad = buildingsWithLoad.has('_community')
        for (let idx = 0; idx < buildings.length; idx++) {
          const b = buildings[idx]
          if (!buildingsWithLoad.has(b.id) && !hasCommunityLevelLoad) {
            const annualKwh = estimateBuildingAnnualKwh(b, community)
            const dailyKwh = annualKwh / 365
            // Use index-based seed offset to ensure unique profiles per building
            const seed = 42 + idx * 7 + (b.id ? b.id.charCodeAt(4) || 0 : 0)
            const syntheticLoad = czechLoadProfile(dailyKwh, seed, community?.metadata?.country)
            aggregatedLoad = aggregatedLoad.map((v, i) => v + syntheticLoad[i])
            perBuildingLoad[b.id] = syntheticLoad
          }
        }

        if (!hasAnyRealLoad && buildings.length === 0) {
          return {
            ...buildSyntheticBalance(community, season, batteryKwh),
            source: 'synthetic',
            sourceLabel: 'Synthetic Profile',
          }
        }

        // --- PV: per-asset deduplication + rooftop PV synthetic ---
        // Group PV datasets by building_id (null = community-level).
        const pvBySource = {}
        for (const ds of pvDatasets) {
          const key = ds.building_id || '_community'
          if (!pvBySource[key]) pvBySource[key] = []
          pvBySource[key].push(ds)
        }

        let aggregatedPV = new Array(24).fill(0)
        const pvSourcesCovered = new Set()

        for (const [key, dsList] of Object.entries(pvBySource)) {
          const profiles = []
          for (const ds of dsList) {
            try {
              const vals = await getValues(ds.id, { ...dateRange, limit: 168 })
              const valuesArr = vals?.values || vals || []
              if (Array.isArray(valuesArr) && valuesArr.length > 0) {
                profiles.push(computeHourlyAverage(valuesArr))
              }
            } catch { /* skip */ }
          }
          if (profiles.length > 0) {
            const avgProfile = profiles[0].map((_, i) =>
              +(profiles.reduce((sum, p) => sum + p[i], 0) / profiles.length).toFixed(3)
            )
            aggregatedPV = aggregatedPV.map((v, i) => v + avgProfile[i])
            pvSourcesCovered.add(key)
            if (key !== '_community') perBuildingPV[key] = avgProfile
          }
        }

        // Add synthetic PV for rooftop PV assets without generation datasets,
        // but only if there are no community-level PV datasets covering all buildings.
        const hasCommunityLevelPV = pvSourcesCovered.has('_community')
        for (let idx = 0; idx < buildings.length; idx++) {
          const b = buildings[idx]
          if (pvSourcesCovered.has(b.id) || hasCommunityLevelPV) continue
          const rooftopPV = (b.building_assets || []).find(a => a.asset_type === 'rooftop_pv')
          if (rooftopPV) {
            const kwp = rooftopPV.specs?.capacity_kwp || 5
            const seed = 43 + idx * 7 + (b.id ? b.id.charCodeAt(4) || 0 : 0)
            const synPV = czechPVProfile(kwp, season, seed, communityLatitude)
            aggregatedPV = aggregatedPV.map((v, i) => v + synPV[i])
            perBuildingPV[b.id] = synPV
          }
        }

        // Add synthetic PV for community PV farms without datasets
        if (!pvSourcesCovered.has('_community')) {
          for (let idx = 0; idx < assets.length; idx++) {
            const a = assets[idx]
            if (['solar_pv_farm', 'ground_mounted_pv'].includes(a.asset_type)) {
              const kwp = a.specs?.capacity_kwp || 10
              const seed = 44 + idx * 7 + (a.id ? a.id.charCodeAt(4) || 0 : 0)
              const synPV = czechPVProfile(kwp, season, seed, communityLatitude)
              aggregatedPV = aggregatedPV.map((v, i) => v + synPV[i])
            }
          }
        }

        // Compute battery dispatch and grid flows
        const balance = computeBalanceFromArrays(aggregatedLoad, aggregatedPV, batteryKwh)

        // Build per-building energy balances (no community battery — only rooftop PV)
        const perBuildingEB = {}
        for (const b of buildings) {
          const bLoad = perBuildingLoad[b.id] || new Array(24).fill(0)
          const bPV = perBuildingPV[b.id] || new Array(24).fill(0)
          // Per-building: no battery (battery is community-level)
          perBuildingEB[b.id] = computeBalanceFromArrays(bLoad, bPV, 0)
        }

        return {
          ...balance,
          perBuildingEB,
          source: hasAnyRealLoad ? 'db' : 'synthetic',
          sourceLabel: hasAnyRealLoad
            ? `Seasonal Average (${season})`
            : 'Synthetic Profile',
        }
      } catch {
        return {
          ...buildSyntheticBalance(community, season, batteryKwh),
          source: 'synthetic',
          sourceLabel: 'Synthetic Profile',
        }
      }
    },
    enabled: !!communityId && !!community,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Build a synthetic energy balance using the existing generators.
 */
function buildSyntheticBalance(community, season, batteryKwh) {
  const countryRef = getCountryDefaults(community)
  const buildings = community?.buildings || []
  const assets = community?.community_assets || []

  const totalAnnualKwh = buildings.reduce((sum, b) => {
    const realLoad = b.metadata?.annual_load_kwh
    if (realLoad && realLoad > 0) return sum + realLoad
    const units = b.metadata?.num_units || b.num_units || 1
    const base = b.building_type === 'commercial' ? countryRef.commercialKwh
      : b.building_type === 'municipal' ? countryRef.municipalKwh
      : countryRef.residentialKwhPerUnit * units
    return sum + base
  }, 0)

  const totalPvKwp = assets
    .filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))
    .reduce((sum, a) => sum + (a.specs?.capacity_kwp || 0), 0)
    + buildings.reduce((sum, b) =>
      sum + (b.building_assets || [])
        .filter(a => a.asset_type === 'rooftop_pv')
        .reduce((s, a) => s + (a.specs?.capacity_kwp || 0), 0)
    , 0)

  return czechEnergyBalance({
    dailyKwh: totalAnnualKwh / 365,
    pvKwp: totalPvKwp,
    batteryKwh,
    season,
    seed: community?.id?.charCodeAt(0) || 42,
    country: community?.metadata?.country,
    latitude: community?.metadata?.latitude,
  })
}

/**
 * Compute battery dispatch and grid flows from load and PV arrays.
 * Logic mirrors czechEnergyBalance() battery dispatch.
 *
 * @param {number[]} load - 24-hour load profile (kW)
 * @param {number[]} pv - 24-hour PV generation profile (kW)
 * @param {number} batteryKwh - Battery capacity
 * @returns {object} { hours, load, pv, wind, gridImport, gridExport, batteryCharge, batteryDischarge }
 */
function computeBalanceFromArrays(load, pv, batteryKwh) {
  const hours = Array.from({ length: 24 }, (_, i) => i)
  const wind = hours.map(() => 0)

  if (batteryKwh <= 0) {
    // No battery: simple net metering
    const gridImport = hours.map((_, h) => +Math.max(0, load[h] - pv[h]).toFixed(2))
    const gridExport = hours.map((_, h) => +Math.max(0, pv[h] - load[h]).toFixed(2))
    return {
      hours, load, pv, wind, gridImport, gridExport,
      batteryCharge: hours.map(() => 0),
      batteryDischarge: hours.map(() => 0),
    }
  }

  const maxPower = batteryKwh * 0.5
  let soc = batteryKwh * 0.40
  const batteryCharge = []
  const batteryDischarge = []
  const gridImport = []
  const gridExport = []

  for (let h = 0; h < 24; h++) {
    const net = pv[h] + wind[h] - load[h]
    let ch = 0, dis = 0, imp = 0, exp = 0

    if (net > 0) {
      const room = Math.max(0, batteryKwh * 0.90 - soc)
      ch = Math.min(net, maxPower, room)
      soc += ch * 0.94
      exp = Math.max(0, net - ch)
    } else {
      const deficit = Math.abs(net)
      const avail = Math.max(0, soc - batteryKwh * 0.10)
      dis = Math.min(deficit, maxPower, avail)
      soc -= dis / 0.94
      imp = Math.max(0, deficit - dis * 0.94)
    }

    batteryCharge.push(+ch.toFixed(2))
    batteryDischarge.push(+dis.toFixed(2))
    gridImport.push(+imp.toFixed(2))
    gridExport.push(+exp.toFixed(2))
  }

  return { hours, load, pv, wind, gridImport, gridExport, batteryCharge, batteryDischarge }
}

/**
 * Compute monthly bills from real dataset consumption data and tariffs.
 *
 * Returns the last 6 complete months of bills, calculated from:
 * - Monthly load totals (from load datasets)
 * - Effective tariffs (from tariff datasets or country defaults)
 * - Self-sufficiency reduction
 *
 * @param {string|null} communityId
 * @param {object|null} community - Full community object
 * @param {object|null} kpis - KPIs from communityKPIs()
 */
export function useCommunityMonthlyBills(communityId, community, kpis) {
  return useQuery({
    queryKey: ['communityMonthlyBills', communityId],
    queryFn: async () => {
      if (!communityId || !community) return null

      const buildings = community.buildings || []
      const currency = community.currency || 'EUR'
      const meta = community.metadata || {}

      if (buildings.length === 0) return null

      // Determine last 6 complete months
      const now = new Date()
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const last6Months = []
      for (let i = 6; i >= 1; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
        last6Months.push({
          label: monthNames[d.getMonth()],
          year: d.getFullYear(),
          month: d.getMonth(),
        })
      }
      const monthLabels = last6Months.map(m => m.label)

      try {
        const datasets = await getDatasets({ community_id: communityId })
        const allDatasets = Array.isArray(datasets) ? datasets : []
        const loadDatasets = allDatasets.filter(
          d => d.kind === 'load' || (d.dataset_type && d.dataset_type.toLowerCase().includes('load'))
        )

        // No load data at all → empty chart
        if (loadDatasets.length === 0) return null

        // Resolve tariffs: historical dataset → static metadata
        const importTariffDs = allDatasets.find(d =>
          d.dataset_type === 'energy_tariff_data' ||
          (d.kind === 'tariff' && !d.dataset_type?.startsWith('day_ahead_') && !d.dataset_type?.includes('feed_in'))
        )

        // Pre-load monthly tariff averages from historical dataset (one per month)
        const monthlyTariffAvg = {}
        if (importTariffDs) {
          for (const m of last6Months) {
            const startDate = new Date(Date.UTC(2025, m.month, 1))
            const endDate = new Date(Date.UTC(2025, m.month + 1, 1))
            try {
              const vals = await getValues(importTariffDs.id, {
                start_time: startDate.toISOString(),
                end_time: endDate.toISOString(),
                limit: 744,
              })
              const valuesArr = vals?.values || vals || []
              if (Array.isArray(valuesArr) && valuesArr.length > 0) {
                const sum = valuesArr.reduce((s, v) => s + (v.value != null ? v.value : 0), 0)
                monthlyTariffAvg[m.month] = sum / valuesArr.length
              }
            } catch { /* use fallback */ }
          }
        }

        // Static tariff fallback (from community setup)
        const staticTariff = meta.import_tariff || 0
        if (!staticTariff && Object.keys(monthlyTariffAvg).length === 0) {
          // No tariffs at all — can't compute bills
          return null
        }

        const buildingBills = []

        // Check for community-level load datasets (building_id is null)
        const communityLoadDatasets = loadDatasets.filter(d => !d.building_id)
        const perBuildingLoadDatasets = loadDatasets.filter(d => d.building_id)

        // Helper: fetch monthly kWh from a dataset
        const fetchMonthlyKwh = async (dsId, month) => {
          const startDate = new Date(Date.UTC(2025, month.month, 1))
          const endDate = new Date(Date.UTC(2025, month.month + 1, 1))
          try {
            const vals = await getValues(dsId, {
              start_time: startDate.toISOString(),
              end_time: endDate.toISOString(),
              limit: 744,
            })
            const valuesArr = vals?.values || vals || []
            if (Array.isArray(valuesArr) && valuesArr.length > 0) {
              return valuesArr.reduce((sum, v) => sum + (v.value != null ? v.value : 0), 0)
            }
          } catch { /* skip */ }
          return null
        }

        if (perBuildingLoadDatasets.length > 0) {
          // Per-building datasets: match each building to its dataset
          for (const b of buildings) {
            const bLoadDs = perBuildingLoadDatasets.find(d => d.building_id === b.id)
            if (!bLoadDs) continue

            const monthlyData = []
            for (const m of last6Months) {
              const monthlyKwh = await fetchMonthlyKwh(bLoadDs.id, m)
              if (monthlyKwh != null) {
                const tariff = monthlyTariffAvg[m.month] || staticTariff
                monthlyData.push(Math.round(monthlyKwh * tariff))
              } else {
                monthlyData.push(0)
              }
            }

            buildingBills.push({
              name: `${b.building_id} - ${b.building_type || 'residential'}`,
              data: monthlyData,
            })
          }
        } else if (communityLoadDatasets.length > 0) {
          // Community-level datasets: aggregate all, split proportionally across buildings
          // First, get total community monthly kWh from all community-level load datasets
          const communityMonthlyKwh = []
          for (const m of last6Months) {
            let totalKwh = 0
            for (const ds of communityLoadDatasets) {
              const kwh = await fetchMonthlyKwh(ds.id, m)
              if (kwh != null) totalKwh += kwh
            }
            communityMonthlyKwh.push(totalKwh)
          }

          if (buildings.length > 0) {
            // Split by annual_consumption_kwh ratios
            const annualLoads = buildings.map(b =>
              b.metadata?.annual_consumption_kwh || estimateBuildingAnnualKwh(b, community)
            )
            const totalAnnual = annualLoads.reduce((s, v) => s + v, 0) || 1

            for (let i = 0; i < buildings.length; i++) {
              const share = annualLoads[i] / totalAnnual
              const monthlyData = communityMonthlyKwh.map((kwh, mi) => {
                const tariff = monthlyTariffAvg[last6Months[mi].month] || staticTariff
                return Math.round(kwh * share * tariff)
              })
              buildingBills.push({
                name: `${buildings[i].building_id} - ${buildings[i].building_type || 'residential'}`,
                data: monthlyData,
              })
            }
          } else {
            // No buildings defined — show as single "Community" bar
            const monthlyData = communityMonthlyKwh.map((kwh, mi) => {
              const tariff = monthlyTariffAvg[last6Months[mi].month] || staticTariff
              return Math.round(kwh * tariff)
            })
            buildingBills.push({ name: 'Community Total', data: monthlyData })
          }
        }

        if (buildingBills.length === 0) return null

        return {
          months: monthLabels,
          buildings: buildingBills,
          currency,
          source: 'db',
        }
      } catch {
        return null
      }
    },
    enabled: !!communityId && !!community,
    staleTime: 5 * 60 * 1000,
  })
}
